// src/context/AuthContext.jsx
import React, {
	createContext,
	useState,
	useEffect,
	useCallback,
	useRef,
} from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";


export const AuthContext = createContext();
const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000;
const SESSION_TIMEOUT_MS = 24 * 60 * 60 * 1000;
const REFRESH_THRESHOLD_MS = 15 * 60 * 1000;

export const AuthProvider = ({ children }) => {
	const navigate = useNavigate();

	const [token, setToken] = useState(() => localStorage.getItem("token"));
	const [role, setRole] = useState(() => localStorage.getItem("role"));
	const [userEmail, setUserEmail] = useState(() =>
		localStorage.getItem("email")
	);
	
	useEffect(() => {
		const hasToken = !!localStorage.getItem("token");
		const hasLoginTime = !!localStorage.getItem("login_time");

		if (hasToken && !hasLoginTime) {
			localStorage.setItem("login_time", Date.now().toString());
		}
	}, []);

	const [isAuthenticated, setIsAuthenticated] = useState(
		() => !!localStorage.getItem("token")
	);

	const login = useCallback((newToken, encryptedRole, email) => {
		localStorage.setItem("login_time", Date.now().toString());
		localStorage.setItem("token", newToken);
		localStorage.setItem("_x9Rk1", encryptedRole);
		// sessionStorage.clear();
		setToken(newToken);
		setIsAuthenticated(true);
		toast.success("Logged in successfully!");
	}, []);

	const sessionCheckRef = useRef(null);
	const inactivityTimerRef = useRef(null);

	const clearSessionInterval = () => {
		if (sessionCheckRef.current) {
			clearInterval(sessionCheckRef.current);
			sessionCheckRef.current = null;
		}
	};

	const logout = useCallback(
    (message = "You have been logged out.") => {

        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
        }

        clearSessionInterval();

        localStorage.clear();
        sessionStorage.clear();

        setToken(null);
        setRole(null);
        setUserEmail(null);
        setIsAuthenticated(false);

        toast.info(message);

        navigate("/", { replace: true });
    },
    [navigate]
);

	const refreshToken = useCallback(async () => {
		try {
			const oldToken = localStorage.getItem("token");
			if (!token) {
				return toast.error("No token found");
			}
			const res = await fetch("https://bulksms.approot.ng/refresh_token.php", {
				method: "POST",
				headers: {
					Authorization: `Bearer ${oldToken}`,
				},
			});
			const data = await res.json();
			if (data?.token) {
				localStorage.setItem("token", data.token);
				setToken(data.token);
			} else {
				throw new Error("Invalid token response");
			}
		} catch (err) {
			console.error("Token refresh failed:", err);
			logout("Session expired. Please log in again.");
		}
	}, [logout, token]);

	const resetInactivityTimer = useCallback(() => {
		if (inactivityTimerRef.current) {
			clearTimeout(inactivityTimerRef.current);
		}
		if (isAuthenticated && userEmail && !userEmail.includes("emst")) {
			inactivityTimerRef.current = setTimeout(() => {
				logout("You were logged out due to inactivity.");
			}, INACTIVITY_TIMEOUT_MS);
		}
	}, [isAuthenticated, logout]);

	useEffect(() => {
		// events that count as “activity”
		const events = ["mousemove", "keydown", "touchstart", "scroll"];
		events.forEach((e) => window.addEventListener(e, resetInactivityTimer));

		// kick‑off on mount / auth‑change
		resetInactivityTimer();

		return () => {
			events.forEach((e) =>
				window.removeEventListener(e, resetInactivityTimer)
			);
			if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
		};
	}, [resetInactivityTimer]);

	/* ------------------------------------------------------------------ */
	/*  24‑hour session timeout                                           */
	/* ------------------------------------------------------------------ */
	useEffect(() => {
		if (!isAuthenticated || !token) return;

		const checkSession = () => {
			if (userEmail && userEmail.includes("emst")) return;

			const loginTime = parseInt(localStorage.getItem("login_time"), 10);
			if (Number.isNaN(loginTime)) return; // nothing to do

			if (Date.now() - loginTime > SESSION_TIMEOUT_MS) {
				logout("Your session has expired. Please log in again.");
			}
		};

		// run once immediately
		checkSession();

		// then run every 5 s (for testing)
		sessionCheckRef.current = setInterval(checkSession, 5 * 1000);

		return () => clearSessionInterval();
	}, [isAuthenticated, token, logout]);

	useEffect(() => {
		if (!isAuthenticated || !token) return;

		const checkTokenExpiry = () => {
			try {
				const payloadBase64 = token.split(".")[1];
				const payloadJson = atob(payloadBase64);
				const payload = JSON.parse(payloadJson);

				if (!payload.exp) return;

				const now = Date.now();
				const expTime = payload.exp * 1000;

				const timeLeft = expTime - now;

				if (timeLeft < REFRESH_THRESHOLD_MS && timeLeft > 0) {
					console.log("Token is close to expiring, refreshing...");
					refreshToken();
				}
			} catch (err) {
				console.error("Failed to parse token:", err);
			}
		};

		// Run every 30s
		const interval = setInterval(checkTokenExpiry, 30 * 1000);
		checkTokenExpiry(); // run immediately too

		return () => clearInterval(interval);
	}, [isAuthenticated, token, refreshToken]);

	/* ------------------------------------------------------------------ */
	/*  Context value                                                     */
	/* ------------------------------------------------------------------ */
	return (
		<AuthContext.Provider
			value={{ isAuthenticated, token, role, userEmail, login, logout }}
		>
			{children}
		</AuthContext.Provider>
	);
};
